import { useEffect, useRef, useState } from "react";
import { Play, Pause, Loader2, Clapperboard, Volume2 } from "lucide-react";
import { api } from "../lib/api";

export const VlogPlayer = ({ slug, fallbackCover }) => {
  const [vlog, setVlog] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [seg, setSeg] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    setVlog(null); setAudioSrc(null); setPlaying(false); setSeg(0);
    api.get(`/vlog/${slug}`).then(({ data }) => setVlog(data)).catch(() => {});
  }, [slug]);

  const toggle = async () => {
    if (!vlog) return;
    if (playing) { audioRef.current?.pause(); return; }
    if (!audioSrc) {
      setLoadingAudio(true);
      try {
        const { data } = await api.post(`/vlog/${slug}/audio`);
        const src = `data:audio/mp3;base64,${data.audio_b64}`;
        setAudioSrc(src);
        setTimeout(() => { audioRef.current.src = src; audioRef.current.play(); }, 50);
      } catch { /* ignore */ }
      finally { setLoadingAudio(false); }
    } else {
      audioRef.current?.play();
    }
  };

  const onTime = () => {
    const a = audioRef.current;
    if (!a || !a.duration || !vlog?.segments?.length) return;
    setSeg(Math.min(vlog.segments.length - 1, Math.floor((a.currentTime / a.duration) * vlog.segments.length)));
  };

  if (!vlog) return null;

  return (
    <section className="my-10 rounded-3xl glass overflow-hidden" data-testid="vlog-section">
      <div className="p-5 border-b border-border flex items-center gap-2">
        <Clapperboard className="h-5 w-5 text-primary" />
        <h2 className="font-head text-xl font-extrabold">Watch the Vlog</h2>
        <span className="ml-auto text-xs text-muted-foreground flex items-center gap-1"><Volume2 className="h-3.5 w-3.5" /> AI narration</span>
      </div>

      <div className="relative aspect-video bg-black">
        <img src={vlog.cover || fallbackCover} alt={vlog.title} className={`h-full w-full object-cover transition-transform duration-[6000ms] ${playing ? "scale-110" : "scale-100"}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40" />
        <button data-testid="vlog-play-btn" onClick={toggle} disabled={loadingAudio}
          className="absolute inset-0 grid place-items-center group">
          <span className="h-16 w-16 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-2xl shadow-primary/40 group-hover:scale-110 transition-transform">
            {loadingAudio ? <Loader2 className="h-7 w-7 animate-spin" /> : playing ? <Pause className="h-7 w-7" /> : <Play className="h-7 w-7 ml-1" />}
          </span>
        </button>
        <div className="absolute bottom-0 inset-x-0 p-5">
          <p data-testid="vlog-caption" className="text-white font-head font-semibold text-lg md:text-xl leading-snug drop-shadow-lg">
            {vlog.segments?.[seg]}
          </p>
        </div>
      </div>

      <audio ref={audioRef} onTimeUpdate={onTime}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); setSeg(0); }} />

      <details className="p-5" data-testid="vlog-transcript">
        <summary className="cursor-pointer font-semibold text-sm text-primary">Read full transcript</summary>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{vlog.script}</p>
      </details>
    </section>
  );
};
